import {
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
  getDoc,
  addDoc,
  doc,
  updateDoc,
  Timestamp,
} from 'firebase/firestore';
import {db, auth} from '../firebase';
import {request} from './apiClient';
import {markSessionPushed} from './chartingService';
import {withTimeout} from '../utils/withTimeout';
import {normalizePatientMrn} from '../utils/patientMrn';
import {normalizePatientSearchText} from '../utils/patientSearch';
import {withFirestoreTimeout} from './firestoreTimeout';
import {
  dlStartSession,
  dlAppendSessionEvents,
  dlEndSession,
  dlRateSession,
  dlSetEncounterPatient,
  dlCreateEncounterNote,
  dlUpdateNoteSection,
  dlMarkNotePushed,
  dlSoftDeleteSession,
  dlUpsertPatient,
  dlDeletePatient,
  dlRestoreSession,
  dlSetEncounterSummary,
  dlUpdateSessionTags,
  fetchSessionEvents,
  fetchEncounterDetail,
  fetchPatientsPage,
  fetchPatientRow,
  lookupPatientRows,
  fetchRecentPatientRows,
  fetchSessionRow,
  fetchPatientDocumentRows,
  createPatientDocumentRow,
  deletePatientDocumentRow,
  fetchRemovedSessions,
  sessionEventsToLegacyMessages,
  legacyFieldsToTags,
  mintedEncounterIdFor,
  patientRosterRowToLegacyPatientDoc,
  recentSessionRowToLegacyConversation,
  encounterSessionSummaryToLegacyConversation,
} from './dataLayerClient';

const COLLECTION = 'Patient';
const PAGE_SIZE = 20;

// Max time to wait for the post-generation Firestore write before treating the
// client as wedged. A healthy write lands in well under a second; this only
// trips when the SDK is in its failed state, so it can be generous yet still
// rescue the UI from an infinite spinner.
const NOTE_SAVE_TIMEOUT_MS = 30_000;

// Shared "Demo Company" catch-all tenant (fallback for trial/demo signups).
// Its members are unrelated strangers, so the patient list is isolated to
// each user's own patients — never the shared facility/company roster. This
// is a client-side UX rule only (no server enforcement). Keep in sync with
// the backend FALLBACK_COMPANY_ID.
const DEMO_COMPANY_ID = 'fKU3FfHdRPNmby7c5GeX';

function normalizeTimestamp(value: any) {
  if (!value) return new Date(0);
  if (typeof value.toDate === 'function') return value.toDate();
  return new Date(value);
}

function normalizeLinkedEhrPatientId(value: any) {
  return (
    String(value ?? '')
      .trim()
      .split('/')
      .filter(Boolean)
      .pop() || ''
  );
}

function linkedEhrPatientIdVariants(value: any) {
  const id = normalizeLinkedEhrPatientId(value);
  if (!id) return [];
  return [id, `Patient/${id}`];
}

function patientMatchesScope(
  patient: Record<string, any>,
  {companyId = null, facilityId = null}: Record<string, any> = {},
) {
  if (companyId && patient.company_id && patient.company_id !== companyId) {
    return false;
  }
  if (facilityId && patient.facility_id && patient.facility_id !== facilityId) {
    return false;
  }
  return true;
}

function isInlineSyntheticId(id: unknown): boolean {
  return typeof id === 'string' && id.startsWith('inline-');
}

// ─── Schema V2 helpers ───────────────────────────────────────────────

// 'YYYY-MM-DD' for the dlUpsertPatient wire format, from whatever the legacy
// callers hand us (a local-midnight Date from the date input, a Firestore
// Timestamp on a merged existing doc, or an already-formatted string).
function toYmdDateString(value: any): string | null {
  if (!value) return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
    const parsed = new Date(trimmed);
    if (Number.isNaN(parsed.getTime())) return null;
    return toYmdDateString(parsed);
  }
  const date =
    typeof value.toDate === 'function' ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function toEpochMs(value: any): number | undefined {
  if (!value) return undefined;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value.toDate === 'function') return value.toDate().getTime();
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? undefined : d.getTime();
}

// sessionId → encounterId, learned from REST reads (fetchSessionEvents
// responses carry the session's encounterId). Lets encounter-id resolution
// skip a network round-trip for sessions whose transcript was just loaded —
// which is every note-save and end-of-session path.
const encounterIdBySessionId = new Map<string, string>();

export function rememberSessionEncounterId(
  sessionId: unknown,
  encounterId: unknown,
) {
  if (
    typeof sessionId === 'string' &&
    sessionId &&
    typeof encounterId === 'string' &&
    encounterId
  ) {
    encounterIdBySessionId.set(sessionId, encounterId);
  }
}

// Resolve the V2 encounter id for a legacy conversation id: an explicit hint
// wins, then the session's own `encounterId` (from the REST session-events
// read — cached by getSessionMessages, fetched on demand otherwise), then the
// minted `enc-{conversationId}` twin every legacy-created session gets
// server-side. No client Firestore involved.
async function resolveEncounterIdForConversation(
  conversationId: string,
  {encounterId = null}: {encounterId?: string | null} = {},
): Promise<string> {
  if (encounterId) return encounterId;
  const cached = encounterIdBySessionId.get(conversationId);
  if (cached) return cached;
  try {
    const response = await fetchSessionEvents(conversationId);
    if (response?.encounterId) {
      rememberSessionEncounterId(conversationId, response.encounterId);
      return String(response.encounterId);
    }
  } catch {
    /* fall through to the minted twin */
  }
  return mintedEncounterIdFor(conversationId);
}

// V2 EncounterNote id keyed by every client-side handle we've seen for the
// note (the noteId itself and, for history-recovered notes, the legacy
// dictated_note message id whose structured_output carries
// `encounter_note_id`). Lets updateSavedNoteSectionsForSession /
// updatePushedSnapshotForSession route V2 notes through the callables while
// pre-V2 notes keep the legacy message-update path — without changing the
// call-site contract (they keep passing `messageId`).
const v2NoteIdByHandle = new Map<string, string>();

// Last sections we know the server has for a V2 note, so per-section edits
// only send the sections that actually changed (one dlUpdateNoteSection call
// per changed section instead of one per section per blur).
const lastPersistedNoteSections = new Map<string, Record<string, any>>();

function registerV2NoteHandle(handle: unknown, noteId: unknown) {
  if (typeof handle === 'string' && handle && typeof noteId === 'string') {
    v2NoteIdByHandle.set(handle, noteId);
  }
}

function seedNoteRegistryFromMessages(messages: any[] = []) {
  for (const message of messages) {
    const structured =
      message?.structured_output || message?.structuredOutput || null;
    const noteId =
      structured?.encounter_note_id || structured?.encounterNoteId || null;
    if (noteId && message?.id) registerV2NoteHandle(message.id, noteId);
  }
}

/**
 * Adopt a transcript that arrived via a composed view read (the
 * session-detail view endpoint) instead of getSessionMessages: cache the
 * session's encounter binding for later writes and seed the V2 note registry
 * from any dictated_note messages, exactly like getSessionMessages does.
 */
export function adoptSessionTranscript(
  conversationId: string,
  {
    encounterId = null,
    messages = [],
  }: {encounterId?: string | null; messages?: any[]} = {},
) {
  rememberSessionEncounterId(conversationId, encounterId);
  seedNoteRegistryFromMessages(messages);
}

export function resolveV2NoteId(handle: unknown): string | null {
  if (typeof handle !== 'string' || !handle) return null;
  return v2NoteIdByHandle.get(handle) || null;
}

function messageContentType(message: any = {}) {
  return message.content_type || message.contentType || '';
}

function noteTemplateId(message: any = {}) {
  const structured =
    message.structured_output || message.structuredOutput || {};
  return (
    structured.template_id ||
    structured.templateId ||
    structured.note_type ||
    structured.noteType ||
    'soap'
  );
}

function messageDedupeKey(message: any = {}) {
  const type = messageContentType(message);
  const timestampMs = normalizeTimestamp(message.timestamp).getTime();
  if (
    type === 'dictated_note' ||
    message.structured_output ||
    message.structuredOutput
  ) {
    return `note:${noteTemplateId(message)}:${timestampMs}`;
  }

  const input =
    message.input_text || message.inputText || message.content || '';
  const output = message.output_text || message.outputText || '';
  const speaker = message.explicit_meaning || message.explicitMeaning || '';
  if (!input && !output) {
    const id = message.id ? String(message.id) : '';
    if (id && !isInlineSyntheticId(id)) return `id:${id}`;
  }
  return [
    'msg',
    type,
    timestampMs,
    input,
    output,
    Boolean(message.is_doctor || message.isDoctor),
    speaker,
  ].join('|');
}

export function mergeSessionMessagesForDisplay(...sources: any[][]) {
  const byKey = new Map();
  sources
    .flat()
    .filter(Boolean)
    .forEach(message => {
      byKey.set(messageDedupeKey(message), message);
    });
  return Array.from(byKey.values()).sort(
    (a, b) => normalizeTimestamp(a.timestamp) - normalizeTimestamp(b.timestamp),
  );
}

/**
 * Patient service — Firestore CRUD mirroring the iOS Patient model.
 *
 * Firestore field names use snake_case (e.g. first_name, preferred_language)
 * to stay consistent with the iOS CodingKeys.
 */
export const patientService = {
  // ─── Fetch ────────────────────────────────────────────────

  /**
   * Fetch patients with pagination.
   *
   * Schema V2: the roster comes from GET /api/v2/patients — company-scoped,
   * ordered by updated_at desc, cursor-paginated server-side. Tenant scoping
   * AND the special isolation rules (limited users / shared Demo Company
   * members only see patients they created) are enforced by the backend from
   * the bearer token; the `facilityId`/`userId`/`userRole` parameters are
   * kept for signature stability but no longer drive the query.
   *
   * Returns { patients, lastDoc, hasMore } exactly like before — `lastDoc`
   * is now an opaque `{__v2Cursor}` object callers pass back unchanged for
   * the next page (they never introspected the Firestore snapshot before).
   */
  async fetchPatients(
    _facilityId: any,
    _userId: any,
    lastDocument: any = null,
    _companyId: any = null,
    _userRole: any = null,
  ) {
    const cursor =
      lastDocument && typeof lastDocument.__v2Cursor === 'string'
        ? lastDocument.__v2Cursor
        : null;
    const {patients: rows, nextCursor} = await fetchPatientsPage({
      limit: PAGE_SIZE,
      cursor,
    });
    return {
      patients: rows.map(patientRosterRowToLegacyPatientDoc),
      lastDoc: nextCursor ? {__v2Cursor: nextCursor} : null,
      hasMore: Boolean(nextCursor),
    };
  },

  // ─── Live subscription ────────────────────────────────────
  //
  // REST-backed replacement for the old Firestore onSnapshot roster stream.
  // Emits an initial page immediately, then re-polls GET /api/v2/patients on
  // an interval so a patient added/edited on another device still shows up
  // without a manual refresh (coarser than the push stream it replaces, but
  // no client Firestore). Scoping is server-side (see fetchPatients).
  //
  // `onChange` receives `{ patients, lastDoc, hasMore }` (same shape as
  // `fetchPatients`). `onError` fires when the initial load fails — callers
  // fall back to a one-time load there (poll failures after a successful
  // emit are logged and retried on the next tick instead).
  //
  // Returns an unsubscribe function.
  subscribeToPatients(
    _facilityId: any,
    _userId: any,
    options: Record<string, any> = {},
    onChange: (result: any) => void,
    onError?: (err: any) => void,
  ) {
    const {windowSize = 200, pollIntervalMs = 60_000} = options;
    let cancelled = false;
    let hasEmitted = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const load = async () => {
      try {
        // Page (contract page size 50) until the requested window is filled
        // or the roster runs out — mirrors the old 200-row live window.
        const rows: any[] = [];
        let cursor: string | null = null;
        do {
          const page = await fetchPatientsPage({limit: 50, cursor});
          rows.push(...page.patients);
          cursor = page.nextCursor;
        } while (cursor && rows.length < windowSize);
        if (cancelled) return;
        hasEmitted = true;
        onChange?.({
          patients: rows
            .slice(0, windowSize)
            .map(patientRosterRowToLegacyPatientDoc),
          lastDoc: cursor ? {__v2Cursor: cursor} : null,
          hasMore: Boolean(cursor),
        });
      } catch (err) {
        if (cancelled) return;
        if (!hasEmitted) {
          onError?.(err);
          return; // initial load failed — caller owns the fallback
        }
        console.warn(
          '[patientService] roster poll failed; retrying next tick',
          (err as any)?.message,
        );
      }
      if (!cancelled) {
        timer = setTimeout(load, pollIntervalMs);
      }
    };

    load();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  },

  // ─── Single patient ───────────────────────────────────────
  //
  // Schema V2: GET /api/v2/patients/:patientId (company-gated server-side),
  // mapped to the legacy snake_case doc shape. Rows carry notes/created_by
  // and the external EHR id (`fhir_patient_id`), so `updatePatient`'s
  // merge-over-current-doc and resolveEhrPatientId keep working.

  async getPatient(patientId: string) {
    if (!patientId) return null;
    const row = await fetchPatientRow(patientId);
    if (!row) return null;
    return patientRosterRowToLegacyPatientDoc(row);
  },

  // ─── Bulk fetch by IDs (for hydrating recents) ────────────
  //
  // Schema V2: POST /api/v2/patients/lookup {patientIds} — one keyed batch
  // read server-side (company-scoped). Missing IDs are simply omitted from
  // the returned map (callers treat absence as "not found / no longer
  // accessible" rather than failing).
  async fetchPatientsByIds(patientIds: string[]) {
    if (!patientIds?.length) return {};
    const ids = [...new Set(patientIds.filter(Boolean).map(String))];
    if (!ids.length) return {};
    const result: Record<string, any> = {};
    try {
      const rows = await lookupPatientRows({patientIds: ids.slice(0, 100)});
      for (const row of rows) {
        result[row.patientId] = patientRosterRowToLegacyPatientDoc(row);
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.warn(
          'fetchPatientsByIds lookup failed:',
          (err as any)?.message,
        );
      }
    }
    return result;
  },

  // Schema V2: POST /api/v2/patients/lookup {ehrPatientIds} — the server
  // resolves external ids through PatientEHRLink (companyId +
  // externalPatientId), which also covers legacy free-form ehrType links the
  // old five-field client fan-out chased. Result map stays keyed by the
  // normalized (bare) EHR id. `options` company/facility scoping is retained
  // as a belt-and-braces client filter, though rows are already
  // company-scoped server-side.
  async fetchPatientsByLinkedEhrIds(
    ehrPatientIds: string[],
    options: Record<string, any> = {},
  ) {
    if (!ehrPatientIds?.length) return {};
    const normalizedIds = [
      ...new Set(
        ehrPatientIds.map(normalizeLinkedEhrPatientId).filter(Boolean),
      ),
    ];
    if (!normalizedIds.length) return {};
    const result: Record<string, any> = {};
    try {
      const rows = await lookupPatientRows({
        ehrPatientIds: normalizedIds.slice(0, 100),
      });
      for (const row of rows) {
        const patient = patientRosterRowToLegacyPatientDoc(row);
        if (!patientMatchesScope(patient, options)) continue;
        const linkedId = normalizeLinkedEhrPatientId(row.ehrPatientId);
        if (!linkedId || !normalizedIds.includes(linkedId)) continue;
        result[linkedId] = patient;
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.warn(
          'fetchPatientsByLinkedEhrIds lookup failed:',
          (err as any)?.message,
        );
      }
    }
    return result;
  },

  // ─── Recent patients for the current user ─────────────────
  //
  // Schema V2: GET /api/v2/patients/recent — the server walks the caller's
  // newest sessions to their encounters' patients, dedupes and hydrates the
  // local Patient docs in one call (windowed like the legacy last-60-
  // conversations scan). The caller/company scope comes from the bearer
  // token, so the `userId`/`companyId`/`pageSize` parameters are accepted
  // for signature stability but no longer sent.
  //
  // Returns: Array<{
  //   ehrPatientId: string|null,
  //   patientId: string|null,
  //   patientName: string|null,
  //   lastSessionAt: Date|null,
  //   patient: <legacy Patient doc>,   // hydrated row (new in V2)
  // }>
  // ordered by `lastSessionAt` desc, capped at `limitCount`. The try/catch
  // keeps the picker degrading gracefully (empty list, no UI error).
  async fetchRecentPatientsForUser(
    userId: string,
    {limitCount = 10}: Record<string, any> = {},
  ) {
    if (!userId) return [];
    let rows;
    try {
      rows = await fetchRecentPatientRows({
        limit: Math.min(Math.max(limitCount, 1), 50),
      });
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.warn(
          'fetchRecentPatientsForUser request failed:',
          (err as any)?.message,
        );
      }
      return [];
    }

    return rows.map(row => {
      const patient = patientRosterRowToLegacyPatientDoc(row);
      const name = [row.firstName, row.lastName]
        .map(part => String(part || '').trim())
        .filter(Boolean)
        .join(' ');
      return {
        ehrPatientId: row.ehrPatientId
          ? normalizeLinkedEhrPatientId(row.ehrPatientId)
          : null,
        patientId: row.patientId,
        patientName: name || null,
        lastSessionAt: row.lastSessionAt ? new Date(row.lastSessionAt) : null,
        patient,
      };
    });
  },

  // ─── Duplicate lookup (create flow) ───────────────────────
  //
  // Scans the company roster for a patient whose normalized first+last name
  // matches the form. Used as a pre-create warning, not a uniqueness
  // constraint: DOB only disqualifies a candidate when both sides have one
  // and they differ (a name match where either side lacks a DOB still warns).
  // The roster is updatedAt-desc, so the double-entry twins this exists to
  // catch (audit 2026-07-21: pairs created seconds to days apart) sit in the
  // first pages; the page cap bounds cost on large rosters rather than
  // guaranteeing a full scan.

  async findDuplicatePatientByName({
    firstName,
    lastName,
    dateOfBirth = null,
    excludePatientId = null,
  }: {
    firstName: string;
    lastName: string;
    dateOfBirth?: any;
    excludePatientId?: string | null;
  }) {
    const wantFirst = normalizePatientSearchText(firstName);
    const wantLast = normalizePatientSearchText(lastName);
    if (!wantFirst || !wantLast) return null;
    const wantDob = toYmdDateString(dateOfBirth);

    const maxPages = 8; // 8 × 50 = the 400 most recently updated patients
    let cursor: string | null = null;
    for (let page = 0; page < maxPages; page += 1) {
      const {patients: rows, nextCursor} = await fetchPatientsPage({
        limit: 50,
        cursor,
        q: String(lastName || '').trim(),
      });
      const match = rows.find(row => {
        if (excludePatientId && row.patientId === excludePatientId)
          return false;
        if (normalizePatientSearchText(row.firstName) !== wantFirst)
          return false;
        if (normalizePatientSearchText(row.lastName) !== wantLast) return false;
        const rowDob = toYmdDateString(row.dateOfBirth);
        return !(wantDob && rowDob && wantDob !== rowDob);
      });
      if (match) return patientRosterRowToLegacyPatientDoc(match);
      if (!nextCursor) return null;
      cursor = nextCursor;
    }
    return null;
  },

  // ─── Create ───────────────────────────────────────────────
  //
  // Schema V2: goes through the dlUpsertPatient callable, which writes the
  // V2 Patient fields + the legacy snake_case mirror (and PatientEHRLink when
  // an EHR link is supplied) in one server-side transaction. Caller identity/
  // company/facility are resolved server-side. The legacy-shaped return value
  // is preserved so `usePatientCreate` consumers keep working unchanged.
  //
  // Idempotent re-submits: pass `data.id` (client-minted) and the server
  // upserts that doc instead of minting a new one — the duplicate-Patient
  // guard for double-submits and error-then-retry (see usePatientCreate).

  async createPatient(data: Record<string, any>) {
    const fields = {
      firstName: data.firstName || '',
      lastName: data.lastName || '',
      preferredLanguage: data.preferredLanguage || null,
      preferredDialect: data.preferredDialect || null,
      dateOfBirth: toYmdDateString(data.dateOfBirth),
      gender: data.gender || null,
      phoneNumber: data.phoneNumber || null,
      email: data.email || null,
      mrn: normalizePatientMrn(data.mrn) || null,
      notes: data.notes || null,
    };
    const {patientId} = await dlUpsertPatient({
      ...(data.id ? {patientId: data.id} : {}),
      fields,
      ...(data.ehrLink?.ehrType && data.ehrLink?.externalPatientId
        ? {
            ehrLink: {
              ehrType: data.ehrLink.ehrType,
              externalPatientId: data.ehrLink.externalPatientId,
            },
          }
        : {}),
    });

    const now = Timestamp.now();
    return {
      id: patientId,
      first_name: fields.firstName,
      last_name: fields.lastName,
      preferred_language: fields.preferredLanguage,
      preferred_dialect: fields.preferredDialect,
      date_of_birth: data.dateOfBirth || null,
      gender: fields.gender,
      phone_number: fields.phoneNumber,
      email: fields.email,
      mrn: fields.mrn,
      notes: fields.notes,
      created_at: now,
      updated_at: now,
      created_by: data.createdBy,
      company_id: data.companyId || null,
      facility_id: data.facilityId,
    };
  },

  // ─── Update ───────────────────────────────────────────────
  //
  // Schema V2: dlUpsertPatient requires the full field set (it's an upsert,
  // not a patch), so merge the caller's partial snake_case `updates` over the
  // current doc before calling. Unknown/extra legacy keys are dropped — the
  // server owns the canonical field list.

  async updatePatient(patientId: string, updates: Record<string, any>) {
    const existing: Record<string, any> =
      (await this.getPatient(patientId).catch(() => null)) || {};
    const merged = {...existing, ...updates};
    const pick = (snake: string, camel: string) =>
      updates[snake] !== undefined
        ? updates[snake]
        : (merged[snake] ?? merged[camel] ?? null);

    await dlUpsertPatient({
      patientId,
      fields: {
        firstName: pick('first_name', 'firstName') || '',
        lastName: pick('last_name', 'lastName') || '',
        preferredLanguage: pick('preferred_language', 'preferredLanguage'),
        preferredDialect: pick('preferred_dialect', 'preferredDialect'),
        dateOfBirth: toYmdDateString(pick('date_of_birth', 'dateOfBirth')),
        gender: pick('gender', 'gender'),
        phoneNumber: pick('phone_number', 'phoneNumber'),
        email: pick('email', 'email'),
        mrn: normalizePatientMrn(pick('mrn', 'mrn')) || null,
        notes: pick('notes', 'notes'),
      },
    });
  },

  // ─── Delete ───────────────────────────────────────────────

  // Schema V2: DELETE /api/v2/patients/:patientId removes the Patient doc
  // and its PatientEHRLink server-side (company-checked). The old client
  // step that unlinked the user's Conversations (`patient_id: deleteField`)
  // is retired with the per-conversation patient stamps — patient identity
  // is canonical on the Encounter, and timeline reads come from
  // GET /api/v1/patients/:id/encounters, which simply stops returning rows
  // for a deleted patient.
  async deletePatient(patientId: string) {
    await dlDeletePatient({patientId});
  },

  // (getPatientStats / getPatientSessions / getPatientSessionsUnified —
  // the per-user `Conversation.patient_id ==` query family — are RETIRED.
  // Patient timelines read GET /api/v1/patients/:id/encounters via
  // encounterService.fetchEncountersForPatient; the per-conversation patient
  // stamp those queries depended on is no longer written.)

  // Public wrapper over the encounter-id resolution used by the write paths:
  // explicit hint → the session's own binding (cached from REST reads,
  // fetched on demand) → the minted `enc-{sessionId}` twin.
  async resolveEncounterIdForSession(
    conversationId: string,
    {encounterId = null}: {encounterId?: string | null} = {},
  ) {
    return resolveEncounterIdForConversation(conversationId, {encounterId});
  },

  // ─── Fetch messages for a conversation ────────────────────
  //
  // Schema V2: past-session transcripts come from the backend
  // `GET /api/v1/sessions/:id/events` (the V2 event stream), mapped to the
  // legacy Message view-model so every renderer keeps its props. The legacy
  // Firestore fan-out below remains as the fallback for:
  //   - environments where the REST endpoint isn't deployed yet (it ships to
  //     prod with the next backend deploy),
  //   - sessions whose V2 event stream is empty (pre-backfill history and
  //     very old inline `messages` arrays).

  async getSessionMessages(conversationId: string) {
    try {
      const response = await fetchSessionEvents(conversationId);
      // Remember the session's encounter binding so later writes (summary
      // updates, patient links, note saves) resolve their encounter id
      // without another read.
      rememberSessionEncounterId(conversationId, response?.encounterId);
      const events = Array.isArray(response?.events) ? response.events : [];
      if (events.length > 0) {
        const messages = sessionEventsToLegacyMessages(events);
        // Remember which dictated_note messages already have a V2
        // EncounterNote so note edits/pushes route through the callables.
        seedNoteRegistryFromMessages(messages);
        return messages;
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.warn(
          '[patientService] V2 events read failed; falling back to Firestore:',
          (err as any)?.message,
        );
      }
    }
    const messages = await this.getSessionMessagesLegacy(conversationId);
    seedNoteRegistryFromMessages(messages);
    return messages;
  },

  // Legacy Firestore transcript fan-out (inline messages + `Messages` +
  // lowercase `messages`). Kept as the fallback path for getSessionMessages —
  // do not add new callers.
  async getSessionMessagesLegacy(conversationId: string) {
    const normalizeInlineMessages = (messages: any[] = []) =>
      [...messages]
        .map((message, index) => ({
          id: message.id || `inline-${index}`,
          ...message,
        }))
        .sort(
          (a, b) =>
            normalizeTimestamp(a.timestamp) - normalizeTimestamp(b.timestamp),
        );

    const fetchFromCollection = async (collectionName: any) => {
      try {
        const ref = collection(db, collectionName, conversationId, 'Messages');
        const q = query(ref, orderBy('timestamp', 'asc'));
        const snapshot = await withFirestoreTimeout(getDocs(q), {
          label: `getSessionMessages:${collectionName}/Messages`,
        });
        return snapshot.docs.map((d: any) => ({id: d.id, ...d.data()}));
      } catch (err) {
        console.warn(
          `${collectionName} messages index may be missing, fetching unsorted:`,
          err.message,
        );
        const ref = collection(db, collectionName, conversationId, 'Messages');
        const snapshot = await withFirestoreTimeout(getDocs(ref), {
          label: `getSessionMessages:${collectionName}/Messages:unsorted`,
        });
        const msgs = snapshot.docs.map((d: any) => ({id: d.id, ...d.data()}));
        msgs.sort((a, b) => {
          const ta = a.timestamp?.toDate?.() || new Date(0);
          const tb = b.timestamp?.toDate?.() || new Date(0);
          return ta - tb;
        });
        return msgs;
      }
    };

    let inlineMessages = [];
    const conversationSnap = await withFirestoreTimeout(
      getDoc(doc(db, 'Conversation', conversationId)),
      {label: 'getSessionMessages:Conversation'},
    );
    if (conversationSnap.exists()) {
      const conversationData = conversationSnap.data();
      if (
        Array.isArray(conversationData.messages) &&
        conversationData.messages.length > 0
      ) {
        inlineMessages = normalizeInlineMessages(conversationData.messages);
      }
    }

    const collectionMessages = await fetchFromCollection('Conversation');
    let lowercaseMessages = [];
    if (collectionMessages.length === 0) {
      try {
        const lowercaseRef = collection(
          db,
          'Conversation',
          conversationId,
          'messages',
        );
        const lowercaseSnapshot = await withFirestoreTimeout(
          getDocs(query(lowercaseRef, orderBy('timestamp', 'asc'))),
          {label: 'getSessionMessages:Conversation/messages'},
        );
        lowercaseMessages = lowercaseSnapshot.docs.map((d: any) => ({
          id: d.id,
          ...d.data(),
        }));
      } catch (err) {
        console.warn(
          'Conversation/messages index may be missing, fetching unsorted:',
          err.message,
        );
        const lowercaseRef = collection(
          db,
          'Conversation',
          conversationId,
          'messages',
        );
        const lowercaseSnapshot = await withFirestoreTimeout(
          getDocs(lowercaseRef),
          {label: 'getSessionMessages:Conversation/messages:unsorted'},
        );
        lowercaseMessages = lowercaseSnapshot.docs.map((d: any) => ({
          id: d.id,
          ...d.data(),
        }));
        lowercaseMessages.sort(
          (a, b) =>
            normalizeTimestamp(a.timestamp) - normalizeTimestamp(b.timestamp),
        );
      }
    }
    return mergeSessionMessagesForDisplay(
      inlineMessages,
      collectionMessages,
      lowercaseMessages,
    );
  },

  // ─── Fetch a single conversation document ─────────────────
  //
  // Schema V2: GET /api/v2/sessions/:sessionId — single-session METADATA (no
  // transcript), mapped to the legacy Conversation view-model. Returns null
  // when the session doesn't exist. Callers use this to treat the session as
  // the source of truth for whose session this is, rather than trusting
  // navigation/route state that can go stale and mis-attribute a note.
  async getConversation(conversationId: string) {
    if (!conversationId) return null;
    const row = await fetchSessionRow(conversationId);
    if (!row) return null;
    rememberSessionEncounterId(conversationId, row.encounterId);
    return recentSessionRowToLegacyConversation(row);
  },

  // ─── Removed sessions (trash view) ──────────────────────────
  //
  // Schema V2: the caller's own soft-deleted sessions come from
  // GET /api/v2/sessions/removed (providerUserId == uid, isRemoved,
  // newest-first), mapped to the legacy Conversation view-model the trash
  // list renders. Replaces the old per-user Firestore page scan with the
  // client-side `is_removed` filter.
  async getRemovedSessions({
    limit: limitCount = 100,
    startDate = null,
    endDate = null,
  }: Record<string, any> = {}) {
    const rows = await fetchRemovedSessions({
      limit: Math.min(limitCount, 100),
      startDate,
      endDate,
    });
    return rows.map(row => ({
      ...recentSessionRowToLegacyConversation(row),
      is_removed: true,
    }));
  },

  // ─── Soft-delete / restore a session ────────────────────────
  //
  // We never hard-delete Conversation docs from the client. Both directions
  // are single server-side transactions now:
  //   - remove:  DELETE /api/v2/sessions/:id (dlSoftDeleteSession) — flags
  //     the Session removed, drops it from the Encounter aggregates, mirrors
  //     legacy `is_removed`.
  //   - restore: POST /api/v2/sessions/:id/restore (dlRestoreSession) —
  //     clears the flag, recomputes status, refreshes aggregates, clears the
  //     legacy mirror.
  async setSessionRemoved(conversationId: string, isRemoved: boolean) {
    if (!conversationId) return;
    if (isRemoved) {
      await dlSoftDeleteSession({sessionId: conversationId});
      return;
    }
    await dlRestoreSession({sessionId: conversationId});
  },

  archiveSession(conversationId: string) {
    return this.setSessionRemoved(conversationId, true);
  },

  restoreSession(conversationId: string) {
    return this.setSessionRemoved(conversationId, false);
  },

  // ─── Scribe conversations (medical_scribing workflow) ───────

  /**
   * Fetch scribe conversations for a user, ordered by start_time desc.
   * Returns { conversations, lastDoc, hasMore }.
   */
  async fetchScribeConversations(
    userId: string,
    lastDocument: any = null,
    pageSize: number = 15,
  ) {
    const ref = collection(db, 'Conversation');
    let q;
    if (lastDocument) {
      q = query(
        ref,
        where('user_id', '==', userId),
        where('workflow_type', '==', 'medical_scribing'),
        orderBy('start_time', 'desc'),
        startAfter(lastDocument),
        limit(pageSize),
      );
    } else {
      q = query(
        ref,
        where('user_id', '==', userId),
        where('workflow_type', '==', 'medical_scribing'),
        orderBy('start_time', 'desc'),
        limit(pageSize),
      );
    }

    const snapshot = await getDocs(q);
    const conversations = snapshot.docs
      .map((d: any) => ({id: d.id, ...d.data()}))
      .filter(c => c.is_removed !== true);

    return {
      conversations,
      lastDoc: snapshot.docs[snapshot.docs.length - 1] || null,
      hasMore: snapshot.docs.length === pageSize,
    };
  },

  // ─── Custom scribe templates (personal + org-level) ──────────
  //
  // DELIBERATELY still direct (rules-scoped) Firestore reads: template
  // WRITES already go through /api/v1/scribe/templates below; the reads stay
  // client-side until a list endpoint exists.
  // TODO(schema-v2): needs GET /api/v1/scribe/templates to migrate.

  /**
   * Fetch personal templates for a user.
   */
  async fetchCustomTemplates(userId: string) {
    const ref = collection(db, 'ScribeTemplate');
    try {
      const q = query(
        ref,
        where('user_id', '==', userId),
        orderBy('created_at', 'desc'),
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map((d: any) => ({id: d.id, ...d.data()}));
    } catch (err) {
      console.warn(
        'ScribeTemplate index may be missing, fetching unordered:',
        err.message,
      );
      const snapshot = await getDocs(
        query(ref, where('user_id', '==', userId)),
      );
      const docs = snapshot.docs.map((d: any) => ({id: d.id, ...d.data()}));
      docs.sort((a, b) => {
        const ta = a.created_at?.toDate?.() || new Date(0);
        const tb = b.created_at?.toDate?.() || new Date(0);
        return tb - ta;
      });
      return docs;
    }
  },

  /**
   * Fetch org-level templates for a company.
   */
  async fetchOrgTemplates(companyId: string | null) {
    if (!companyId) return [];
    const ref = collection(db, 'ScribeTemplate');
    try {
      const q = query(
        ref,
        where('company_id', '==', companyId),
        orderBy('created_at', 'desc'),
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map((d: any) => ({id: d.id, ...d.data()}));
    } catch (err) {
      console.warn('ScribeTemplate org index may be missing:', err.message);
      const snapshot = await getDocs(
        query(ref, where('company_id', '==', companyId)),
      );
      const docs = snapshot.docs.map((d: any) => ({id: d.id, ...d.data()}));
      docs.sort((a, b) => {
        const ta = a.created_at?.toDate?.() || new Date(0);
        const tb = b.created_at?.toDate?.() || new Date(0);
        return tb - ta;
      });
      return docs;
    }
  },

  /**
   * Fetch both personal + org templates and merge (deduplicated).
   */
  async fetchAllTemplates(userId: string, companyId: string | null) {
    const [personal, org] = await Promise.all([
      this.fetchCustomTemplates(userId),
      this.fetchOrgTemplates(companyId),
    ]);
    const seen = new Set();
    const merged = [];
    for (const t of [...org, ...personal]) {
      if (!seen.has(t.id)) {
        seen.add(t.id);
        merged.push(t);
      }
    }
    return merged;
  },

  /**
   * Save a scribe template (personal or org-level) via the backend API.
   * The backend uses the Admin SDK so Firestore security rules are bypassed;
   * authorization is enforced in application code.
   */
  async saveCustomTemplate(
    userId: string,
    template: Record<string, any>,
    {companyId, facilityId, isOrgLevel}: Record<string, any> = {},
  ) {
    const sections = (template.sections || []).map((s: any, idx: any) => {
      if (typeof s === 'string')
        return {name: s, prompt: '', required: true, order: idx};
      const sec: Record<string, any> = {
        name: s.name || '',
        prompt: s.prompt || '',
        required: s.required !== false,
        order: s.order ?? idx,
      };
      if (s.template_content || s.templateContent)
        sec.template_content = s.template_content || s.templateContent;
      if (s.ehr_key) sec.ehr_key = s.ehr_key;
      if (s.writable != null) sec.writable = s.writable;
      return sec;
    });

    if (template.id) {
      const data = await request(`/api/v1/scribe/templates/${template.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          name: template.name,
          description: template.description || '',
          sections,
          is_dynamic: template.is_dynamic || false,
          category: template.category || 'medical',
          specialty: template.specialty || null,
          specialty_category: template.specialty_category || null,
          display_order: template.display_order ?? null,
          is_active: template.is_active !== false,
        }),
      });
      return {...template, ...data, id: template.id};
    }

    const data = await request('/api/v1/scribe/templates', {
      method: 'POST',
      body: JSON.stringify({
        name: template.name,
        description: template.description || '',
        sections,
        is_dynamic: template.is_dynamic || false,
        category: template.category || 'medical',
        specialty: template.specialty || null,
        specialty_category: template.specialty_category || null,
        display_order: template.display_order ?? null,
        is_active: template.is_active !== false,
        is_org_level: isOrgLevel || false,
      }),
    });
    return data;
  },

  async deleteCustomTemplate(templateId: string) {
    await request(`/api/v1/scribe/templates/${templateId}`, {method: 'DELETE'});
  },

  /**
   * Turn a pasted sample/example note into a properly-sectioned template
   * (Chief Complaint, HPI, ROS, Exam, Assessment, Plan, …) via the backend's
   * one-shot AI parse. This is the recommended way to build a custom template:
   * it splits the note into real sections instead of cramming the whole thing
   * into one giant section (which times out at generation time).
   *
   * Returns the parsed shape: { name, description, specialty,
   * specialty_category, sections: [{ name, prompt, required, order }], ... }.
   */
  async parseTemplateFromRaw(
    rawText: string,
    {hint}: Record<string, any> = {},
  ) {
    return request('/api/v1/scribe/templates/parse-from-raw', {
      method: 'POST',
      body: JSON.stringify({
        raw_text: rawText,
        ...(hint ? {hint} : {}),
      }),
    });
  },

  // ─── Patient documents (PDF uploads for scribe context) ─────
  //
  // Schema V2: PatientDocument metadata CRUD goes through
  // GET/POST/DELETE /api/v2/patients/:patientId/documents[/:documentId].
  // Rows keep the historical snake_case field names but timestamps are ISO
  // strings now. The Storage object itself stays client-managed exactly as
  // before (create is metadata-before-upload; delete is metadata-only, the
  // caller deletes the Storage file separately).

  /**
   * Create a PatientDocument metadata record (called before/during upload).
   * Identity fields (company/facility/uploader) are stamped server-side from
   * the caller — the legacy `companyId`/`facilityId`/`uploadedBy` params are
   * accepted for signature stability but no longer sent.
   */
  async createPatientDocument(data: Record<string, any>) {
    return createPatientDocumentRow(data.patientId, {
      fileName: data.fileName,
      ...(data.fileType ? {fileType: data.fileType} : {}),
      ...(typeof data.fileSize === 'number' ? {fileSize: data.fileSize} : {}),
      storagePath: data.storagePath,
      ...(data.storageUrl ? {storageUrl: data.storageUrl} : {}),
      ...(data.documentType ? {documentType: data.documentType} : {}),
    });
  },

  /**
   * Fetch all documents for a patient (newest first, server-ordered).
   */
  async fetchPatientDocuments(patientId: string) {
    if (!patientId) return [];
    return fetchPatientDocumentRows(patientId);
  },

  /**
   * Delete a patient document (metadata only; caller should also delete from
   * Storage). Requires the owning patient id for the REST route.
   */
  async deletePatientDocument(patientId: string, documentId: string) {
    await deletePatientDocumentRow(patientId, documentId);
  },

  // ─── Live scribe session management ─────────────────────────

  /**
   * Mint a Conversation id and build its payload WITHOUT writing it to
   * Firestore. The caller is responsible for persisting it later via
   * `persistScribeConversation`. This split lets the scribe session defer the
   * actual write until audio is confirmed flowing, so a start that never
   * captures audio (blocked WS handshake, dead mic, no_audio_frames stall)
   * never leaves an empty Conversation doc behind.
   */
  buildScribeConversation(data: Record<string, any>) {
    const id = doc(collection(db, 'Conversation')).id;
    const now = Timestamp.now();
    const payload = {
      facility_id: data.facilityId || '',
      company_id: data.companyId || '',
      user_id: data.userId,
      start_time: now,
      end_time: null,
      minutes: 0,
      summary: '',
      workflow_type: 'medical_scribing',
      template_type: data.templateId || 'soap',
      specialty: data.specialty || '',
      provider_name: data.providerName || '',
      patient_name: data.patientName || '',
      patient_id: data.patientId || null,
      ver_num: 'web-v1.0',
      userEmail: data.userEmail || '',
      // Encounter linkage — when continuing an existing encounter, this is
      // populated up-front so the conversation is queryable as part of the
      // encounter from the moment it's written. Standalone (legacy) scribe
      // starts simply omit the field, which the grouping helpers treat as
      // an implicit single-conversation encounter.
      ...(data.encounterId ? {encounter_id: data.encounterId} : {}),
    };
    return {id, payload};
  },

  /**
   * Persist a previously-built Conversation via the V2 data layer.
   *
   * Schema V2: one dlStartSession call writes the Session doc, creates or
   * joins the Encounter, mirrors the legacy Conversation doc (same id), and
   * bumps the company counter — atomically, server-side. The legacy payload
   * built by `buildScribeConversation` is translated to the V2 wire shape
   * here so callers keep their existing contract.
   */
  async persistScribeConversation(id: string, payload: Record<string, any>) {
    await dlStartSession({
      sessionId: id,
      encounterId: payload.encounter_id || null,
      tags: legacyFieldsToTags({
        workflowType: payload.workflow_type || 'medical_scribing',
        isTelephonic: payload.isTelephonic === true,
        specialty: payload.specialty || null,
        templateType: payload.template_type || null,
      }),
      status: 'active',
      startedAt: toEpochMs(payload.start_time),
      patientId: payload.patient_id || null,
      ehrPatientId: payload.ehr_patient_id || null,
      patientName: payload.patient_name || null,
      ...(payload.summary ? {summary: payload.summary} : {}),
      ...(payload.visibility === 'private' ? {visibility: 'private'} : {}),
      language: payload.language || null,
      dialect: payload.dialect || null,
    });
    return {id, ...payload};
  },

  async createScribeConversation(data: Record<string, any>) {
    const {id, payload} = this.buildScribeConversation(data);
    return this.persistScribeConversation(id, payload);
  },

  /**
   * Durability net for solo dictation sessions (planner "+" → Dictation).
   *
   * The scribe agent persists only FINALIZED utterances, and Soniox finals
   * lag the live captions by seconds — so a short dictation ended promptly
   * can leave no Session at all (confirmed in prod: the review then opens on
   * a 404 with no patient, summary, or transcript). A dictation is a
   * deliberate act, so after the room closes the client guarantees the
   * record: create the V2 session if the agent didn't (tag `dictation`),
   * backfill the transcript the provider watched on screen, and end it.
   *
   * A 409 from dlStartSession means the agent's ingest ensure-start won —
   * the agent owns the transcript, so nothing is backfilled (appending here
   * too would duplicate lines). Returns {created} accordingly.
   */
  async ensureDictationSessionPersisted({
    conversationId,
    transcripts = [],
    startTime = null,
    elapsedSeconds = 0,
    patientId = null,
    ehrPatientId = null,
    patientName = '',
    specialty = '',
    templateType = 'soap',
  }: Record<string, any>) {
    if (!conversationId) return {created: false};
    try {
      await dlStartSession({
        sessionId: conversationId,
        tags: legacyFieldsToTags({
          workflowType: 'dictation',
          isTelephonic: false,
          specialty: specialty || null,
          templateType: templateType || null,
        }),
        status: 'active',
        startedAt: toEpochMs(startTime || new Date()),
        patientId: patientId || null,
        ehrPatientId: ehrPatientId || null,
        patientName: patientName || null,
      });
    } catch (error: any) {
      if (
        error?.status === 409 ||
        String(error?.message || '').includes('already exists')
      ) {
        return {created: false};
      }
      throw error;
    }

    // We created the session ⇒ the agent persisted nothing; write the local
    // captions as the durable transcript. Shape mirrors the agent's
    // transcript.message events (no speakerRole; diarized label rides
    // explicitMeaning) so downstream note generation reads both identically.
    const entries = (Array.isArray(transcripts) ? transcripts : [])
      .map((t: any) => ({
        text: String(t?.text || '').trim(),
        speaker: t?.speaker || '',
        speakerId: t?.speakerId,
      }))
      .filter(t => t.text);
    if (entries.length > 0) {
      await dlAppendSessionEvents({
        sessionId: conversationId,
        events: entries.map(t => ({
          sessionEventType: 'transcript.message',
          payload: {
            text: t.text,
            ...(t.speakerId !== undefined && t.speakerId !== null
              ? {speakerId: String(t.speakerId)}
              : {}),
            ...(t.speaker ? {explicitMeaning: t.speaker} : {}),
            model: 'scribe-transcription-v1',
          },
        })),
      });
    }

    // Same billing semantics as the agent's scribe finalize: usage-tracked
    // (durationSeconds) but never billable.
    await dlEndSession({
      sessionId: conversationId,
      durationSeconds: Math.max(0, Math.round(elapsedSeconds || 0)),
      billableMinutes: 0,
    });
    return {created: true};
  },

  // (setConversationEncounterId is RETIRED: after-the-fact grouping goes
  // through POST /api/v2/encounters/:id/adopt-session — see
  // encounterService.attachConversationToEncounter /
  // combineConversationsIntoNewEncounter — which stamps the legacy
  // `encounter_id` server-side.)

  // Stamp a session as phone-originated. The scribe agent normally writes
  // `isTelephonic` when a SIP leg joins, but a web dial-out knows the call is
  // telephonic up front — stamping from the client too makes the Phone
  // modality deterministic on every recents/detail surface even if the
  // agent's SIP detection lands late or not at all.
  //
  // Schema V2: POST /api/v2/sessions/:id/tags {add:['telephonic']} — the
  // server merges the tag set (a racing/late agent write can't clobber it)
  // and recomputes the legacy `isTelephonic` mirror column.
  async setConversationTelephonic(conversationId: string) {
    if (!conversationId) return;
    await dlUpdateSessionTags({
      sessionId: conversationId,
      add: ['telephonic'],
    });
  },

  /**
   * Fetch every member session of a given Encounter, ordered
   * chronologically, in the legacy Conversation view-model shape.
   */
  async fetchConversationsForEncounter(
    encounterId: string,
    _options: Record<string, any> = {},
  ) {
    // Schema V2: the encounter's member sessions come from
    // GET /api/v1/encounters/:encounterId (tenancy + session privacy
    // enforced server-side), mapped to the legacy Conversation shape the
    // grouping/aggregation helpers consume. Replaces the old per-doc
    // Firestore fan-out (`conversationIdHints` are no longer needed — the
    // detail response carries the member list).
    //
    // Fidelity note: the REST session summaries carry ids, provider ids,
    // times, durations and tags; per-session `provider_name`/`userEmail`
    // aren't part of the V2 read, so contributor labels degrade to role
    // fallbacks where a name isn't otherwise known.
    if (!encounterId) return [];

    let detail;
    try {
      detail = await fetchEncounterDetail(encounterId, {
        includeSections: false,
      });
    } catch (err) {
      console.warn(
        `[patientService] could not load encounter detail for ${encounterId}`,
        (err as any)?.message,
      );
      return [];
    }

    const sessions = Array.isArray(detail?.sessions) ? detail.sessions : [];
    return sessions
      .map(session =>
        encounterSessionSummaryToLegacyConversation(session, {
          encounterId: detail.encounterId || encounterId,
          patientId: detail.patientId ?? null,
          patientName: detail.patientName || '',
          summary: detail.summary || '',
        }),
      )
      .sort((a, b) => {
        const ta = a.start_time?.getTime?.() ?? 0;
        const tb = b.start_time?.getTime?.() ?? 0;
        return ta - tb;
      });
  },

  async addTranscriptionMessage(
    conversationId: string,
    messageData: Record<string, any>,
  ) {
    // Scribe transcripts are persisted raw so that note generation reads the
    // exact text the patient/provider said. /redact-phi is intentionally NOT
    // called here — see the consumer-site de-identification note in git
    // history. Schema V2: one dlAppendSessionEvents call writes the
    // `transcript.message` event AND its legacy Message mirror (same doc id)
    // server-side, so old readers keep seeing the transcript.
    const text = messageData.text || '';
    const result = await dlAppendSessionEvents({
      sessionId: conversationId,
      events: [
        {
          sessionEventType: 'transcript.message',
          payload: {
            text,
            speakerRole: messageData.isDoctor ? 'provider' : 'patient',
            ...(messageData.speakerId !== undefined &&
            messageData.speakerId !== null
              ? {speakerId: String(messageData.speakerId)}
              : {}),
            ...(messageData.speaker
              ? {explicitMeaning: messageData.speaker}
              : {}),
            ...(typeof messageData.confidence === 'number'
              ? {confidenceScore: messageData.confidence}
              : {}),
            model: 'scribe-transcription-v1',
          },
        },
      ],
    });
    // Legacy-shaped echo so existing callers keep reading the same fields.
    return {
      id: result.eventIds[0],
      from_language: 'English',
      to_language: 'English',
      input_text: text,
      output_text: text,
      is_doctor: Boolean(messageData.isDoctor),
      timestamp: Timestamp.now(),
      explicit_meaning: messageData.speaker || '',
      speaker_id: messageData.speakerId ?? null,
      model: 'scribe-transcription-v1',
      quality_check_state: 'complete',
      is_agent_message: false,
      content_type: 'transcription',
      confidence_score: messageData.confidence ?? null,
    };
  },

  async endScribeConversation(
    conversationId: string,
    data: Record<string, any>,
  ) {
    // Schema V2: dlEndSession flips the Session terminal state, recomputes
    // the Encounter aggregates (including the legacy `status: 'closed'`
    // projection that endScribeEncounterIfFullyClosed used to maintain from
    // the client), updates the legacy Conversation mirror (end_time /
    // minutes / summary), and bills the delta minutes — atomically.
    await dlEndSession({
      sessionId: conversationId,
      ...(typeof data.minutes === 'number'
        ? {billableMinutes: data.minutes}
        : {}),
      ...(typeof data.wallClockSeconds === 'number'
        ? {durationSeconds: data.wallClockSeconds}
        : {}),
      summary: data.summary || '',
    });
  },

  // Mid-session / standalone summary update. Schema V2: PATCH
  // /api/v2/encounters/:encounterId/summary sets the encounter projection
  // summary AND mirrors it onto each member session's legacy Conversation
  // doc in one server transaction — replacing the old direct Conversation
  // `summary` write (which relied on the legacy→V2 sync to propagate).
  //
  // Encounter id resolution: explicit `encounterId` option → the session's
  // own encounter binding (cached from the REST events read) → the minted
  // `enc-{conversationId}` twin. Call sites that know the binding (e.g.
  // end-of-session flows holding `encounterIdAtEnd`) should pass it to skip
  // the lookup.
  //
  // Note for multi-session encounters: this sets the ENCOUNTER summary (and
  // every member's mirror). The end-of-session flows that call it follow up
  // with regenerateEncounterSummary for multi-contributor encounters, which
  // overwrites it with the joint summary — same end state as before.
  async updateScribeConversationSummary(
    conversationId: string,
    summary: string,
    {encounterId = null}: {encounterId?: string | null} = {},
  ) {
    const resolvedEncounterId = await resolveEncounterIdForConversation(
      conversationId,
      {encounterId},
    );
    await dlSetEncounterSummary({
      encounterId: resolvedEncounterId,
      summary,
    });
  },

  async rateScribeSession(
    conversationId: string,
    {rating, reason}: Record<string, any>,
  ) {
    // Schema V2: dlRateSession stamps the Session + legacy mirror. The
    // callable requires an integer 1–5, so the (unused in UI) "clear rating"
    // shape keeps the legacy write.
    const numericRating = Number(rating);
    if (
      Number.isInteger(numericRating) &&
      numericRating >= 1 &&
      numericRating <= 5
    ) {
      await dlRateSession({
        sessionId: conversationId,
        rating: numericRating,
        ratingReason: reason || null,
      });
      return;
    }
    const ref = doc(db, 'Conversation', conversationId);
    await updateDoc(ref, {
      rating: rating || null,
      rating_reason: reason || '',
      rated_at: Timestamp.now(),
    });
  },

  // Goes through the backend: Firestore rules lock client-side Conversation
  // deletes to superadmins, and a client `deleteDoc` would orphan the Messages
  // subcollection anyway. The route authorizes owner / company admin and
  // recursively deletes the transcript with the doc.
  async deleteScribeConversation(conversationId: string) {
    if (!conversationId) throw new Error('conversationId is required');
    await request(`/api/v1/sessions/${encodeURIComponent(conversationId)}`, {
      method: 'DELETE',
    });
  },

  // ─── Link EHR patient ID to conversation ─────────────────────
  //
  // Schema V2: patient identity is canonical on the ENCOUNTER, so both link
  // helpers resolve the session's encounter (explicit hint → the session's
  // own encounter binding → the minted `enc-{sessionId}` twin) and call
  // dlSetEncounterPatient. The server projects the patient onto the
  // Encounter's V2 + legacy fields.
  //
  // The old best-effort `patient_id`/`ehr_patient_id` stamp on the
  // Conversation doc itself is RETIRED: the per-user patient panels now read
  // the timeline from GET /api/v1/patients/:id/encounters instead of
  // querying `Conversation.patient_id ==` directly, so the stamp had no
  // remaining reader.
  async linkEhrPatientToConversation(
    conversationId: string,
    ehrPatientId: string,
    {encounterId = null}: Record<string, any> = {},
  ) {
    if (!conversationId || !ehrPatientId) return;
    const resolvedEncounterId = await resolveEncounterIdForConversation(
      conversationId,
      {encounterId},
    );
    await dlSetEncounterPatient({
      encounterId: resolvedEncounterId,
      patientId: null,
      ehrPatientId,
    });
  },

  // ─── Link local/EHR patient to a conversation ────────────────
  async linkPatientToConversation(
    conversationId: string,
    {
      patientId = null,
      ehrPatientId = null,
      patientName = '',
      encounterId = null,
    }: Record<string, any> = {},
  ) {
    if (!conversationId) return;
    const resolvedEncounterId = await resolveEncounterIdForConversation(
      conversationId,
      {encounterId},
    );
    await dlSetEncounterPatient({
      encounterId: resolvedEncounterId,
      patientId: patientId || null,
      ehrPatientId: ehrPatientId || null,
      patientName: patientName || '',
    });
  },

  // ─── Reassign the patient on an existing session ─────────────
  //
  // Used by the "Change patient" affordance in the session detail views to
  // fix a wrong-patient mistake after the fact. Goes through the backend so
  // we capture an audit trail (`patient_reassignment_history`) and clear
  // stale EHR encounter pointers — Firestore rules let the owner write
  // those fields directly, but doing it server-side gives us a single
  // observable surface for "providers who frequently reassign", lets
  // company admins reassign sessions they didn't create, and keeps the
  // EHR-push-routes assumption that `ehr_encounter_id` is always fresh
  // for the *current* patient honest.
  //
  // Pass `{ patientId: null, ehrPatientId: null, patientName: '' }` to
  // clear the patient link entirely. The backend tolerates either
  // identifier missing — an EHR-only patient won't have a local
  // `patient_id`, and a local-only patient won't have an `ehrPatientId`.
  async reassignSessionPatient(
    conversationId: string,
    {
      patientId = null,
      ehrPatientId = null,
      patientName = '',
      reason = null,
    }: Record<string, any> = {},
  ) {
    if (!conversationId) throw new Error('conversationId is required');
    return request(
      `/api/v1/sessions/${encodeURIComponent(conversationId)}/patient`,
      {
        method: 'POST',
        body: JSON.stringify({
          patientId: patientId || null,
          ehrPatientId: ehrPatientId || null,
          patientName: patientName || '',
          ...(reason ? {reason} : {}),
        }),
      },
    );
  },

  // ─── Persist generated note for a conversation ─────────────
  //
  // First-time persistence for a freshly-generated note.
  //
  // Schema V2: one dlCreateEncounterNote call writes the EncounterNote doc +
  // section subcollection, appends it to the Encounter, and mirrors a legacy
  // `dictated_note` Message onto the source session — so old iOS keeps seeing
  // the note without any client-side Message write. The returned `id` is the
  // V2 noteId; subsequent edits route through `updateSavedNoteSectionsForSession`
  // (which resolves it back to dlUpdateNoteSection via the note registry).
  //
  // The legacy addDoc path below remains as the durability fallback: if the
  // callable fails (session not yet synced to V2, offline blip), the note
  // still lands as a legacy message and the server-side backfill trigger
  // materializes the V2 EncounterNote from it.
  async saveGeneratedNoteForSession(
    conversationId: string,
    note: Record<string, any>,
  ) {
    const now = Timestamp.now();
    const templateId = note.templateId || 'soap';
    const sectionEntries = Object.entries(note.sections || {});
    const formattedText = sectionEntries
      .map(([name, content]) => `${name.toUpperCase()}:\n${content}`)
      .join('\n\n')
      .trim();

    // Legacy-shaped echo (same as the old addDoc payload) so callers keep
    // reading identical fields off the return value.
    const payload = {
      from_language: 'English',
      to_language: 'English',
      input_text: note.rawTranscription || '',
      is_doctor: true,
      output_text: formattedText,
      timestamp: now,
      explicit_meaning: '',
      model: 'scribe-ai-v1',
      quality_check_state: 'complete',
      is_agent_message: false,
      content_type: 'dictated_note',
      structured_output: {
        note_type: templateId,
        sections: note.sections || {},
        template_id: templateId,
        is_complete: true,
      },
    };

    try {
      const encounterId = await resolveEncounterIdForConversation(
        conversationId,
        {encounterId: note.encounterId || null},
      );
      const {noteId} = await withTimeout(
        dlCreateEncounterNote({
          encounterId,
          templateId,
          sourceSessionIds: [conversationId],
          sections: sectionEntries.map(([key, content], index) => ({
            key,
            title: key,
            content: String(content ?? ''),
            order: index,
          })),
        }),
        NOTE_SAVE_TIMEOUT_MS,
        'Saving the note timed out — the connection may be stuck. Please refresh the page and try again.',
      );
      registerV2NoteHandle(noteId, noteId);
      lastPersistedNoteSections.set(noteId, {...(note.sections || {})});

      // Denormalize "a note exists" onto the parent Conversation so the
      // end-of-day charting queue can classify the session without scanning
      // messages (the data layer doesn't stamp this legacy field).
      // Best-effort: never blocks or fails the save.
      updateDoc(doc(db, 'Conversation', conversationId), {
        note_generated_at: now,
      }).catch(() => {});

      return {id: noteId, noteId, ...payload};
    } catch (err) {
      console.warn(
        '[patientService] dlCreateEncounterNote failed; saving legacy dictated_note message',
        (err as any)?.message,
      );
    }

    // ── Legacy fallback (kept for durability during the V2 transition) ──
    const messagesRef = collection(
      db,
      'Conversation',
      conversationId,
      'Messages',
    );
    // Bound the write: when the Firestore JS SDK wedges itself (the
    // `INTERNAL ASSERTION FAILED` bug), addDoc never settles, which previously
    // left the note "Generating…" button spinning forever with no error. Time
    // out so the caller can surface a retry instead of dead-locking.
    const docRef = await withTimeout(
      addDoc(messagesRef, payload),
      NOTE_SAVE_TIMEOUT_MS,
      'Saving the note timed out — the connection may be stuck. Please refresh the page and try again.',
    );
    updateDoc(doc(db, 'Conversation', conversationId), {
      note_generated_at: now,
    }).catch(() => {});
    return {id: docRef.id, ...payload};
  },

  // ─── Update an already-saved generated note in place ──────
  //
  // Used after the initial `saveGeneratedNoteForSession` to persist
  // physician edits to the existing dictated_note message rather than
  // appending a new one on every blur. This keeps the conversation tidy
  // (one canonical note per generation) and preserves the original
  // generation timestamp while recording the latest edit in `edited_at`.
  //
  // Caller is expected to pass the message id returned from
  // `saveGeneratedNoteForSession` (or recovered from
  // `extractAllStructuredNotes`). When no id is available, fall back to
  // `saveGeneratedNoteForSession` to create the message for the first
  // time.
  async updateSavedNoteSectionsForSession({
    conversationId,
    messageId,
    noteId,
    sections,
    templateId,
    rawTranscription,
  }: Record<string, any>) {
    if (!conversationId || !messageId) {
      throw new Error(
        'updateSavedNoteSectionsForSession requires conversationId and messageId',
      );
    }

    // ── Schema V2 path ──
    // Notes created via dlCreateEncounterNote (or recovered from history with
    // a `structured_output.encounter_note_id`) are edited section-by-section
    // through dlUpdateNoteSection, which also rewrites the mirrored legacy
    // dictated_note message server-side. Only the sections that changed since
    // the last persist are sent. Pre-V2 notes (no registry hit) fall through
    // to the legacy in-place message update below.
    const v2NoteId = noteId || resolveV2NoteId(messageId);
    if (v2NoteId) {
      const nextSections: Record<string, any> = sections || {};
      const baseline = lastPersistedNoteSections.get(v2NoteId);
      const changedKeys = Object.keys(nextSections).filter(
        key =>
          !baseline ||
          String(baseline[key] ?? '') !== String(nextSections[key] ?? ''),
      );
      try {
        for (const key of changedKeys) {
          await dlUpdateNoteSection({
            noteId: v2NoteId,
            sectionKey: key,
            content: String(nextSections[key] ?? ''),
          });
        }
        lastPersistedNoteSections.set(v2NoteId, {...nextSections});
        return {id: messageId};
      } catch (err) {
        // If the note only exists in V2 (fresh dlCreateEncounterNote — the
        // handle IS the noteId, there's no legacy message doc to patch),
        // surface the failure so the editor shows the save error. Otherwise
        // fall back to the legacy message update so the edit isn't lost.
        if (messageId === v2NoteId) throw err;
        console.warn(
          '[patientService] dlUpdateNoteSection failed; falling back to legacy message update',
          (err as any)?.message,
        );
      }
    }

    const messageRef = doc(
      db,
      'Conversation',
      conversationId,
      'Messages',
      messageId,
    );
    const resolvedTemplateId = templateId || 'soap';
    const sectionEntries = Object.entries(sections || {});
    const formattedText = sectionEntries
      .map(([name, content]) => `${name.toUpperCase()}:\n${content}`)
      .join('\n\n')
      .trim();

    const updates: Record<string, any> = {
      output_text: formattedText,
      edited_at: Timestamp.now(),
      'structured_output.sections': sections || {},
      'structured_output.template_id': resolvedTemplateId,
      'structured_output.note_type': resolvedTemplateId,
      'structured_output.is_complete': true,
    };
    if (typeof rawTranscription === 'string' && rawTranscription.length > 0) {
      updates.input_text = rawTranscription;
    }

    await updateDoc(messageRef, updates);
    return {id: messageId};
  },

  // ─── Record what was last pushed to the EHR ────────────────
  //
  // Stamps the dictated_note message with a PHI-free fingerprint of the
  // content that just landed in the EHR, plus the push time. This lets the
  // note view detect — even after the provider navigates away and back, or
  // opens the note on another device — that the note has been edited since
  // it was last written to the chart, and prompt a re-push. We store only a
  // short hash (never the note text) so the message doc doesn't balloon.
  //
  // Kept separate from `updateSavedNoteSectionsForSession` on purpose: note
  // edits must NOT touch the pushed fingerprint, otherwise editing would
  // immediately mask the "edited since last push" state we're trying to
  // surface.
  async updatePushedSnapshotForSession({
    conversationId,
    messageId,
    noteId,
    pushedSignature,
  }: Record<string, any>) {
    if (!conversationId || !messageId) {
      throw new Error(
        'updatePushedSnapshotForSession requires conversationId and messageId',
      );
    }
    const pushedAt = Timestamp.now();

    // ── Schema V2 path ──
    // dlMarkNotePushed stamps the EncounterNote (pushedToEhr / ehrPushedAt)
    // AND mirrors `ehr_push_status: 'pushed'` + `ehr_pushed_at` onto the
    // legacy Conversation docs of every source session — replacing the
    // markSessionPushed REST hop below. The legacy message-level
    // last_pushed_signature stamp is kept (when a real message doc exists)
    // because the "edited since last push" detector reads it across devices;
    // V2 has no signature field yet.
    const v2NoteId = noteId || resolveV2NoteId(messageId);
    if (v2NoteId) {
      await dlMarkNotePushed({noteId: v2NoteId});
      if (messageId !== v2NoteId) {
        updateDoc(
          doc(db, 'Conversation', conversationId, 'Messages', messageId),
          {
            last_pushed_signature: pushedSignature || null,
            last_pushed_at: pushedAt,
          },
        ).catch(() => {});
      }
      return {id: messageId, pushedAt};
    }

    // ── Legacy path (pre-V2 notes) ──
    const messageRef = doc(
      db,
      'Conversation',
      conversationId,
      'Messages',
      messageId,
    );
    await updateDoc(messageRef, {
      last_pushed_signature: pushedSignature || null,
      last_pushed_at: pushedAt,
    });

    // Denormalize the push onto the parent Conversation so the end-of-day
    // charting queue can filter out already-charted sessions cheaply. Must go
    // through the backend: `ehr_pushed_at` is in the Firestore rules'
    // protected-fields list, so a direct client update is PERMISSION_DENIED
    // (the old updateDoc here silently failed and pushed rows came back as
    // drafts). Best effort: the message stamp above is the source of truth,
    // this is a read-optimization mirror.
    markSessionPushed(conversationId, {
      signature: pushedSignature || null,
    }).catch(() => {});

    return {id: messageId, pushedAt};
  },
};
